import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Sparkles, X } from 'lucide-react';
import { formatCurrencyWhole } from '../../../utils/formatCurrency.js';
import dashboardService from '../../../services/DashboardService.js';
import CollapsiblePanel from './CollapsiblePanel.js';

const BOOKING_BUCKETS = new Set(['undispatched', 'undispatched_shipments']);

const formatKg = (value) => `${Math.round(Number(value) || 0).toLocaleString('en-IN')} kg`;

const VISIBLE_ROWS = 7;
const ROW_HEIGHT_PX = 28;

const column = (key, titleKey, locationKey) => ({
  key,
  titleKey,
  locationKey,
  headerClass: 'bg-gray-50 text-gray-600',
  bodyClass: 'bg-white-50/70',
  accent: 'border-gray-200',
});

const SHARED_COLUMNS = [
  column('undispatched', 'dashboard.undispatched', 'dashboard.fromLocation'),
  column('in_transit', 'dashboard.inTransit', 'dashboard.toLocation'),
  column('pending_delivery', 'dashboard.pendingDelivery', 'dashboard.toLocation'),
  column('cargo_missing', 'dashboard.cargoMissing', 'dashboard.toLocation'),
];

const HUB_ADMIN_COLUMNS = [
  column('undispatched', 'dashboard.booking', 'dashboard.fromLocation'),
  column('undispatched_shipments', 'dashboard.undispatchedShipments', 'dashboard.toLocation'),
  column('in_transit', 'dashboard.inTransit', 'dashboard.toLocation'),
  column('pending_delivery', 'dashboard.pendingDelivery', 'dashboard.toLocation'),
  column('cargo_missing', 'dashboard.cargoMissing', 'dashboard.toLocation'),
];

const CARGO_SPLIT_ROLES = new Set(['Admin', 'Accountant', 'Agent', 'Hub Admin', 'System Admin']);

const columnsForRole = (role) => (
  CARGO_SPLIT_ROLES.has(role) ? HUB_ADMIN_COLUMNS : SHARED_COLUMNS
);

const MetricCells = ({ row, isMissing }) => (
  isMissing ? (
    <>
      <td className="text-right px-2 py-1">{row.transit || 0}</td>
      <td className="text-right px-2 py-1">{row.delivery || 0}</td>
      <td className="text-right px-3 py-1">{row.qty || 0}</td>
      <td className="text-right px-2 py-1 tabular-nums whitespace-nowrap">{formatKg(row.weight)}</td>
      <td className="text-right px-2 py-1 tabular-nums">{formatCurrencyWhole(row.amount)}</td>
    </>
  ) : (
    <>
      <td className="text-right px-2 py-1">{row.gc_count || 0}</td>
      <td className="text-right px-3 py-1">{row.qty || 0}</td>
      <td className="text-right px-2 py-1 tabular-nums whitespace-nowrap">{formatKg(row.weight)}</td>
      <td className="text-right px-2 py-1 tabular-nums">{formatCurrencyWhole(row.amount)}</td>
    </>
  )
);

const LocationCells = ({ column, row, onOpen, onSuggest, canSuggest, t, nested = false }) => (
  <td className={`px-2 sm:px-3 py-1 ${nested ? 'pl-6' : ''}`}>
    <button
      type="button"
      onClick={() => onOpen(column.key, row.hub_id)}
      className="text-blue-600 hover:underline text-left break-words leading-tight"
    >
      {row.hub_name}
      {row.is_hub ? (
        <span className="hidden sm:inline ml-1 text-[10px] px-1 py-0.5 rounded bg-gray-100 text-gray-500">Hub</span>
      ) : null}
    </button>
    {canSuggest ? (
      <button
        type="button"
        onClick={() => onSuggest(column, row)}
        className="ml-1 inline-flex align-middle text-blue-500 hover:text-blue-700"
        title={t('dashboard.suggestVehicle', { defaultValue: 'Suggest vehicle' })}
        aria-label={t('dashboard.suggestVehicle', { defaultValue: 'Suggest vehicle' })}
      >
        <Sparkles className="w-3 h-3" />
      </button>
    ) : null}
  </td>
);

const CargoColumn = ({ column, data, onOpen, onSuggest }) => {
  const { t } = useTranslation();
  const rows = data?.rows || [];
  const routeGroups = column.key === 'undispatched_shipments' ? (data?.route_groups || []) : [];
  const crossingGroups = column.key === 'undispatched_shipments' ? (data?.crossing_groups || []) : [];
  const totals = data?.totals || {};
  const isMissing = column.key === 'cargo_missing';
  const canSuggest = BOOKING_BUCKETS.has(column.key);
  const [openRoutes, setOpenRoutes] = useState({});

  const toggleRoute = (routeKey) => {
    setOpenRoutes((current) => ({
      ...current,
      [routeKey]: current[routeKey] === false,
    }));
  };

  return (
    <div className="w-full border border-gray-200 rounded-xl overflow-hidden">
      <div className={`flex items-center justify-between gap-2 px-3 py-2 ${column.headerClass}`}>
        <button
          type="button"
          onClick={() => onOpen(column.key, null)}
          className="text-left text-sm font-semibold"
        >
          {t(column.titleKey)}
        </button>
        {canSuggest ? (
          <button
            type="button"
            onClick={() => onSuggest(column, null)}
            className="inline-flex items-center gap-1 shrink-0 rounded-full border border-blue-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-blue-700 hover:bg-blue-50"
          >
            <Sparkles className="w-3 h-3" />
            {t('dashboard.suggestVehicle', { defaultValue: 'Suggest vehicle' })}
          </button>
        ) : null}
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: (VISIBLE_ROWS + 1) * ROW_HEIGHT_PX }}>
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white/70 text-gray-600">
            <tr className="h-7">
              <th className="text-left font-medium px-2 sm:px-3 py-1">{t(column.locationKey)}</th>
              {isMissing ? (
                <>
                  <th className="text-right font-medium px-2 py-1">{t('dashboard.transit')}</th>
                  <th className="text-right font-medium px-2 py-1">{t('dashboard.delivery')}</th>
                </>
              ) : (
                <th className="text-right font-medium px-2 py-1">GC</th>
              )}
                <th className="text-right font-medium px-3 py-1">{t('dashboard.qty')}</th>
              <th className="text-right font-medium px-2 py-1">{t('dashboard.weight', { defaultValue: 'Weight' })}</th>
              <th className="text-right font-medium px-2 py-1">{t('dashboard.totalAmount')}</th>
            </tr>
          </thead>
          <tbody>
            {routeGroups.length > 0 ? routeGroups.map((group) => {
              const open = openRoutes[group.route_key] !== false;
              return (
                <Fragment key={`${column.key}-route-${group.route_key}`}>
                  <tr className="border-t border-gray-200 bg-gray-50 h-7">
                    <td className="px-2 sm:px-3 py-1">
                      <button
                        type="button"
                        onClick={() => toggleRoute(group.route_key)}
                        className="inline-flex items-center gap-1 text-left font-semibold text-gray-800 break-words leading-tight"
                        aria-expanded={open}
                      >
                        <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
                        {group.route_name}
                      </button>
                    </td>
                    <MetricCells row={group} isMissing={isMissing} />
                  </tr>
                  {open ? (group.locations || []).map((row) => (
                    <tr key={`${column.key}-${group.route_key}-${row.hub_id || row.hub_name}`} className="border-t border-gray-100 h-7">
                      <LocationCells
                        column={column}
                        row={row}
                        onOpen={onOpen}
                        onSuggest={onSuggest}
                        canSuggest={canSuggest}
                        t={t}
                        nested
                      />
                      <MetricCells row={row} isMissing={isMissing} />
                    </tr>
                  )) : null}
                </Fragment>
              );
            }) : rows.map((row) => (
              <tr key={`${column.key}-${row.hub_id || 'none'}`} className="border-t border-gray-100 h-7">
                <LocationCells
                  column={column}
                  row={row}
                  onOpen={onOpen}
                  onSuggest={onSuggest}
                  canSuggest={canSuggest}
                  t={t}
                />
                <MetricCells row={row} isMissing={isMissing} />
              </tr>
            ))}
            {crossingGroups.map((group) => {
              const open = openRoutes[group.route_key] !== false;
              return (
                <Fragment key={`${column.key}-crossing-${group.route_key}`}>
                  <tr className="border-t border-gray-200 bg-amber-50 h-7">
                    <td className="px-2 sm:px-3 py-1">
                      <button
                        type="button"
                        onClick={() => setOpenRoutes((current) => ({
                          ...current,
                          [group.route_key]: current[group.route_key] === false,
                        }))}
                        className="inline-flex items-center gap-1 text-left font-semibold text-gray-800 break-words leading-tight"
                        aria-expanded={open}
                      >
                        <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
                        {group.route_name}
                      </button>
                    </td>
                    <MetricCells row={group} isMissing={isMissing} />
                  </tr>
                  {open ? (group.locations || []).map((row) => (
                    <tr key={`${column.key}-${group.route_key}-${row.hub_id || row.hub_name}`} className="border-t border-gray-100 h-7">
                      <td className="px-2 sm:px-3 py-1 pl-6 text-gray-800 break-words leading-tight">
                        {row.hub_name}
                      </td>
                      <MetricCells row={row} isMissing={isMissing} />
                    </tr>
                  )) : null}
                </Fragment>
              );
            })}
            {rows.length === 0 && routeGroups.length === 0 && crossingGroups.length === 0 && (
              <tr>
                <td colSpan={isMissing ? 6 : 5} className="px-3 py-6 text-center text-gray-400">
                  {t('common.noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className={`flex flex-wrap justify-between gap-2 px-3 py-1.5 border-t ${column.accent} text-[11px] font-semibold text-gray-700`}>
        <span>{t('common.total')}</span>
        <span>
          {isMissing
            ? `${t('dashboard.transit')} ${totals.transit || 0} · ${t('dashboard.delivery')} ${totals.delivery || 0} · ${t('dashboard.qty')} ${totals.qty || 0} · ${formatKg(totals.weight)} · ${formatCurrencyWhole(totals.amount)}`
            : `GC ${totals.gc_count || 0} · ${t('dashboard.qty')} ${totals.qty || 0} · ${formatKg(totals.weight)} · ${formatCurrencyWhole(totals.amount)}`}
        </span>
      </div>
    </div>
  );
};

const VehicleSuggestModal = ({ state, onClose }) => {
  const { t } = useTranslation();
  if (!state?.open) return null;
  const vehicles = state.data?.vehicles || [];
  const recommendedId = state.data?.recommended?.id;
  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-lg rounded-xl shadow-lg border border-blue-100 max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('dashboard.suggestVehicle', { defaultValue: 'Suggest vehicle' })}
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-blue-600" />
              {t('dashboard.suggestVehicle', { defaultValue: 'Suggest vehicle' })}
            </h3>
            {state.hubName ? <p className="text-xs text-gray-500 mt-0.5">{state.hubName}</p> : null}
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-500 hover:text-gray-800" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 py-3 overflow-y-auto text-sm">
          {state.loading ? <p className="text-gray-500">Checking vehicle capacity…</p> : null}
          {state.error ? <p className="text-red-600">{state.error}</p> : null}
          {state.data ? (
            <>
              <p className="text-gray-800">{state.data.message}</p>
              <p className="text-xs text-gray-500 mt-1">
                {state.data.gc_count || 0} GC · {formatKg(state.data.total_weight_kg)}
              </p>
              <ul className="mt-3 space-y-2">
                {vehicles.slice(0, 8).map((vehicle) => (
                  <li
                    key={vehicle.id || vehicle.vehicle_number}
                    className={`rounded-lg border px-3 py-2 ${vehicle.id === recommendedId ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-gray-800">
                        {vehicle.vehicle_number}
                        {vehicle.vehicle_name ? ` · ${vehicle.vehicle_name}` : ''}
                      </span>
                      <span className={`text-[11px] font-semibold ${vehicle.fits ? 'text-green-700' : 'text-gray-500'}`}>
                        {vehicle.fits ? 'Fits' : vehicle.has_capacity ? 'Too small' : 'No capacity'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {vehicle.has_capacity
                        ? `Capacity ${formatKg(vehicle.capacity_kg)} · free today ${formatKg(vehicle.available_kg)}`
                        : 'Capacity is not set on this vehicle'}
                    </p>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const CargoOverviewSection = ({
  data,
  onOpenBucket,
  hubFilter = null,
  role = null,
  suggestQuery = {},
}) => {
  const { t } = useTranslation();
  const asOn = data?.as_on;
  const columns = columnsForRole(role);
  const [suggest, setSuggest] = useState(null);

  const openSuggest = async (column, row) => {
    const hubName = row?.hub_name || '';
    setSuggest({ open: true, hubName, loading: true, error: '', data: null });
    try {
      const res = await dashboardService.getCargoVehicleSuggestion({
        ...suggestQuery,
        bucket: column.key,
        ...(row?.hub_id ? { from_hub_id: row.hub_id } : {}),
      });
      setSuggest({
        open: true,
        hubName,
        loading: false,
        error: res?.success ? '' : (res?.error || 'Could not suggest a vehicle'),
        data: res?.data || null,
      });
    } catch (error) {
      setSuggest({
        open: true,
        hubName,
        loading: false,
        error: error?.response?.data?.error || 'Could not suggest a vehicle',
        data: null,
      });
    }
  };

  return (
    <CollapsiblePanel
      title={t('dashboard.overviewCargoCount')}
      defaultOpen
      trailing={(
        <div className="flex items-center gap-2 flex-wrap justify-start sm:justify-end w-full">
          {hubFilter}
          {asOn ? <span className="text-[11px] sm:text-xs text-gray-400 whitespace-nowrap">As on {asOn}</span> : null}
        </div>
      )}
    >
      <div className="flex gap-3 overflow-x-auto pb-1">
        {columns.map((column) => (
          <div key={column.key} className="w-[22rem] sm:w-[24rem] shrink-0">
            <CargoColumn
              column={column}
              data={data?.[column.key]}
              onOpen={onOpenBucket}
              onSuggest={openSuggest}
            />
          </div>
        ))}
      </div>
      <VehicleSuggestModal state={suggest} onClose={() => setSuggest(null)} />
    </CollapsiblePanel>
  );
};

export default CargoOverviewSection;
